<template>
  <div class="inner">
    <Alert v-if="!loading && error" type="error">{{ error }}</Alert>
    <div v-if="loading" class="space-y-2">
      <Skeleton
        v-for="i in 8"
        :key="i"
        shape="rectangle"
        width="100%"
        height="48px" />
    </div>
    <div v-if="accountList && accountList.length > 0">
      <table v-tablesort class="apptable sortable dense --tight-first mt-2">
        <thead>
          <tr>
            <td class="rounded-tl-md rounded-bl-md">Account Name</td>
            <td class="nosort">Coverages</td>
            <td class="rounded-tr-md rounded-br-md">Parent Company</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="account in accountList" :key="account.accountID">
            <td>
              <div class="name-wrapper">
                <router-link
                  class="name"
                  :to="`/account/${
                    account.childAccountID === null ? account.accountID : account.childAccountID
                  }`"
                  data-test="account-name"
                >
                  <div class="font-medium tracking-wide">{{ account.childAccountDisplayName || account.childAccountName || account.accountDisplayName || account.accountName }}</div>
                  <div v-if="account.childAccountID && account.childAccountDisplayName" class="italic font-light text-xs ml-2">{{ account.childAccountName }}</div>
                  <div v-else-if="!account.childAccountID && account.accountDisplayName" class="italic font-light text-xs ml-2">{{ account.accountName }}</div>
                </router-link>
                <Badge
                  v-if="showProspectChip(account)"
                  class="border-sky-500/80 bg-none font-normal text-sky-500/90"
                  variant="outline"
                  data-test="prospect-chip">
                  PROSPECT
                </Badge>
              </div>
            </td>
            <td>
              <div class="coverages m-0 flex flex-wrap gap-1" v-if="account.Coverages.length > 0">
                <Badge
                  v-for="{ key, coverageType } in account.Coverages"
                  :key="key"
                  variant="outline"
                  class="border-gray-300 bg-none font-normal text-primary">
                  {{ coverageType }}
                </Badge>
              </div>
            </td>
            <td>
              <div class="name-wrapper">
                <router-link
                  class="name"
                  :to="`/parent/account/${account.accountID}/overview`"
                  data-test="account-name"
                >
                  <div>{{ account.childAccountID === null ? account.childAccountName : (account.accountDisplayName || account.accountName) }}</div>
                </router-link>
                <Badge
                  v-if="showProspectChip(account)"
                  class="border-sky-500/80 bg-none font-normal text-sky-500/90"
                  variant="outline"
                  data-test="prospect-chip">
                  PROSPECT
                </Badge>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script lang="ts">
import { accountService } from '@/services/api';
import { ClientAccount } from '@/types/AccountSummary';
import { PolicySourceEpic } from '@/utilities/PolicySources';
import { Store } from '@/store';
import { clientViewService } from '@/services/v2/ClientViewService';
import { BenefitsReadCoverageTypes, FeesReadCoverageTypes } from '@/types/CoverageTypes';
import { UserHasPermission, IsUserAClient } from '@/utilities/AuthUtils';
import { UserPermission } from '@/types/UserPermissions';
import Alert from '@/components/v2/Alert.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import Badge from '@/components/ui/Badge.vue';

export default {
  name: 'ClientView',
  components: { Alert, Skeleton, Badge },
  data: () => ({
    accountList: [] as any[],
    loading: true,
    error: '',
  }),
  computed: {
    IsUserAClientAccount() {
      return IsUserAClient(Store.state.user);
    },
  },
  methods: {
    showProspectChip(account: ClientAccount): boolean {
      if (this.IsUserAClientAccount) return false;
      return account.accountStatus === 'P';
    },
    async getClientAccounts(): Promise<ClientAccount[]> {

      let clientAccounts: ClientAccount[];

      if (!Store.state.clientViewState.isClientViewEnabled) {
        clientAccounts = await accountService.getClientAccounts();
      } else {
        const accountIds = Store.state.clientViewState.activeView?.allowedAccountIds;
        if (!accountIds) {
          console.error('No account ids found in client view state');
          return [];
        }

        const accounts = await clientViewService.getClientAccountsByIds(accountIds);

        const transformedAccounts = accounts.reduce((acc, account) => {
          if (!acc.has(account.accountId)) {
            acc.set(account.accountId, {
              accountID: account.parentAccountId ? account.parentAccountId : account.accountId,
              accountName: account.parentAccountId ? account.parentAccountName! : account.accountName,
              accountDisplayName: account.parentAccountId ? account.parentAccountDisplayName : account.accountDisplayName,
              childAccountID: account.parentAccountId ? account.accountId : null,
              childAccountName: account.parentAccountId ? account.accountName : null,
              childAccountDisplayName: account.parentAccountId ? account.accountDisplayName : null,
              accountStatus: account.accountStatus,
              coverageTypes: `${account.coverageType}||${account.policySourceId}`,
              childCoverageTypes: account.parentAccountId ? `${account.coverageType}||${account.policySourceId}` : '',
            });
          } else {
            const found = acc.get(account.accountId);
            found!.coverageTypes += `,${account.coverageType}||${account.policySourceId}`;
            found!.childCoverageTypes += account.parentAccountId ? `,${account.coverageType}||${account.policySourceId}` : '';
            acc.set(account.accountId, found!);
          }
          return acc;
        }, new Map<number, ClientAccount>());

        clientAccounts = Array.from(transformedAccounts.values());
      }

      return clientAccounts
        .map(a => {
          return {
            ...a,
            rowValue: a.childAccountDisplayName || a.childAccountName || a.accountDisplayName || a.accountName,
          };
        })
        .sort((a, b) => a.rowValue.localeCompare(b.rowValue))
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .map(({ rowValue, ...rest }) => rest);

    },
    async getAccountList() {
      this.loading = true;
      const list = await this.getClientAccounts();
      this.accountList = list.map((account) => {
        const coverageTypeStr = account.childAccountID === null ? account.coverageTypes : account.childCoverageTypes;
        const coverageTypeDataList = coverageTypeStr.split(',').filter((t) => t !== '').sort();
        const coverageTypes = coverageTypeDataList
          //re-used from AccountList TODO: refactor
          .map((coverageData) => {
            if (coverageData.includes('||')) {
              const [coverageType, policySourceIDStr] = coverageData.split('||');
              let policySourceID = parseInt(policySourceIDStr);
              if (isNaN(policySourceID)) {
                policySourceID = PolicySourceEpic;
              }
              return { key: coverageData, coverageType, isExternal: policySourceID !== PolicySourceEpic };
            } else {
              return { key: coverageData, coverageType: coverageData, isExternal: false };
            }
          })
          .filter((coverage) => {

            // the API already should do this filtering based on a user's permissions
            // this is mainly to support the "View as Client" use case

            if (FeesReadCoverageTypes.has(coverage.coverageType)) {
              return this.userHasFeesReadPermission();
            }

            if (BenefitsReadCoverageTypes.has(coverage.coverageType)) {
              return this.userHasBenefitsReadPermission();
            }

            return true;
          })
          .sort((a, b) => (a.isExternal === b.isExternal ? 0 : a.isExternal ? 1 : -1));
        return { ...account, Coverages: coverageTypes };
      });
      this.loading = false;
    },
    userHasFeesReadPermission() {
      return UserHasPermission(Store.state.user, UserPermission.FeesRead);
    },
    userHasBenefitsReadPermission() {
      return UserHasPermission(Store.state.user, UserPermission.BenefitsRead);
    },
  },
  created() {
    this.getAccountList();
  },
};
</script>

<style scoped>
.coverages {
  margin-top: 1em;
  max-width: 20vw;
}

</style>
